import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Card, Field, Input, Select, Textarea, PageTitle, StatusChip, DemoBadge, Tag, Meta, Notice, ErrorSummary, cx } from "../components/kit";
import { NoAccess, Loading, ConfirmDialog, HistoryList } from "../components/common";
import { api, parseError } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import ProgrammeEditor, { ACTION_FIELDS, OriginLabel } from "./ProgrammeEditor";
import { VisitsSection, HomeTasksSection, SendSection, ApplicationsSection } from "./ProgrammeOps";

export default function ProgrammeDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [p, setP] = useState(null);
  const [products, setProducts] = useState([]);
  const [denied, setDenied] = useState(false);
  const [errs, setErrs] = useState([]);
  const [missing, setMissing] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [history, setHistory] = useState(null);
  const load = useCallback(async () => {
    try {
      const [r, prods] = await Promise.all([api.get(`/programmes/${id}`), api.get("/products")]);
      setD(r.data);
      setP(r.data.programme);
      setProducts([...prods.data, ...r.data.products.filter((x) => x.user_entry)]);
    } catch (e) { if (parseError(e).status === 404) setDenied(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (denied) return <NoAccess />;
  if (!d || !p) return <Loading />;
  const prog = d.programme;
  const draft = prog.status === "draft";
  const setF = (k) => (e) => setP({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const basis = p.data_use_basis || {};

  const save = async (quiet) => {
    setErrs([]);
    const body = { ...p };
    ["id", "number", "status"].forEach((k) => delete body[k]);
    try {
      const { data } = await api.put(`/programmes/${id}`, body);
      setP(data);
      if (!quiet) toast.success(t("common.saved"));
      return true;
    } catch (e) {
      const x = parseError(e);
      setErrs(x.errors || []);
      toast.error(t(`err.${x.code}`));
      return false;
    }
  };
  const activate = async () => {
    if (!(await save(true))) throw new Error(t("err.validation"));
    try {
      await api.post(`/programmes/${id}/activate`);
      setMissing([]);
      toast.success(t("programme.activated"));
      load();
    } catch (e) {
      const x = parseError(e);
      if (x.code === "activation_blocked") { setMissing(x.missing); return; }
      throw new Error(t(`err.${x.code}`));
    }
  };
  const post = (path, body) => api.post(`/programmes/${id}/${path}`, body).then(load).catch((e) => { throw new Error(t(`err.${parseError(e).code}`)); });

  return (
    <div className="space-y-6">
      <PageTitle title={prog.title || prog.number}
        badge={<>{prog.is_demo && <DemoBadge />}<Tag>{prog.number}</Tag><StatusChip status={prog.status} testid="programme-status" />{prog.archived && <StatusChip status="archived" label={t("status.archived")} />}{prog.current_edition > 0 && <Tag data-testid="programme-edition">{t("programme.edition_n", { n: prog.current_edition })}</Tag>}</>}
        sub={<span><Link className="font-semibold underline" to={`/dogs/${d.dog.id}`}>{d.dog.name}</Link> · {t("common.responsible")}: {prog.responsible_name}</span>}
        right={draft ? <>
          <Btn variant="outline" testid="programme-save-btn" onClick={() => save()}>{t("programme.save_draft")}</Btn>
          <Btn testid="programme-approve-btn" onClick={() => setDlg("activate")}>{t("programme.approve")}</Btn>
        </> : prog.status === "active" ? <>
          <Btn variant="sage" testid="programme-complete-btn" onClick={() => setDlg("complete")}>{t("programme.complete")}</Btn>
          <Btn variant="outline" testid="programme-discontinue-btn" onClick={() => setDlg("discontinue")}>{t("programme.discontinue")}</Btn>
          <Btn variant="ghost" testid="programme-archive-btn" onClick={() => post("archive").catch((e) => toast.error(e.message))}>{t("programme.archive")}</Btn>
        </> : !prog.archived && <Btn variant="outline" testid="programme-archive-btn" onClick={() => post("archive").catch((e) => toast.error(e.message))}>{t("programme.archive")}</Btn>} />
      {missing.length > 0 && <ErrorSummary testid="activation-missing" title={t("programme.activation_blocked")} errors={missing.map((m) => ({ text: t(`activation.${m}`) }))} />}
      <ErrorSummary errors={errs.map((e) => ({ text: `${e.field.replace(/stages\.(\d+)\.actions\.(\d+)\./, (_, a, b) => t("programme.at_action", { s: +a + 1, a: +b + 1 }))} — ${t(`err.${e.code}`)}` }))} />
      {prog.status === "discontinued" && <Notice>{t("common.reason")}: {prog.discontinue_reason}</Notice>}
      {prog.status === "completed" && <Notice tone="ok">{(prog.results || []).map((r, i) => <p key={i}>{r.objective}: {r.result}</p>)}</Notice>}

      <Card className="grid gap-4 md:grid-cols-2">
        <h2 className="md:col-span-2 text-lg font-bold">{t("programme.basics")}</h2>
        {draft ? <>
          <Field label={t("field.title")} htmlFor="pg-title"><Input id="pg-title" data-testid="programme-title-input" value={p.title} onChange={setF("title")} /></Field>
          <Field label={t("programme.review_date")} htmlFor="pg-rd"><Input id="pg-rd" type="date" data-testid="programme-review-date-input" value={p.review_date} onChange={setF("review_date")} /></Field>
          <Field label={t("field.objective")} htmlFor="pg-obj"><Textarea id="pg-obj" data-testid="programme-objective-input" value={p.objective} onChange={setF("objective")} /></Field>
          <Field label={t("programme.baseline")} htmlFor="pg-bl"><Textarea id="pg-bl" data-testid="programme-baseline-input" value={p.baseline_summary} onChange={setF("baseline_summary")} /></Field>
          <Field label={t("programme.observation_criteria")} htmlFor="pg-oc"><Textarea id="pg-oc" data-testid="programme-observation-criteria-input" value={p.observation_criteria} onChange={setF("observation_criteria")} /></Field>
          <Field label={t("programme.completion_conditions")} htmlFor="pg-cc"><Textarea id="pg-cc" data-testid="programme-completion-input" value={p.completion_conditions} onChange={setF("completion_conditions")} /></Field>
          <Field label={t("field.referral_source")} htmlFor="pg-ref"><Input id="pg-ref" data-testid="programme-referral-input" value={p.referral_source} onChange={setF("referral_source")} /></Field>
          <label className="flex min-h-[48px] items-center gap-2 self-end text-sm font-semibold"><input type="checkbox" className="h-5 w-5" data-testid="programme-restrictions-reviewed-checkbox" checked={!!p.restrictions_reviewed} onChange={setF("restrictions_reviewed")} />{t("programme.restrictions_reviewed")}</label>
          <fieldset className="md:col-span-2">
            <legend className="mb-2 text-sm font-semibold">{t("programme.directions")}</legend>
            <div className="flex flex-wrap gap-2">{["A", "B", "C", "D"].map((x) => (
              <label key={x} className={cx("flex min-h-[44px] items-center gap-2 rounded-xl border bg-white px-3 text-sm", p.directions.includes(x) && "border-primary bg-[hsl(var(--peach))]")}>
                <input type="checkbox" className="h-5 w-5" data-testid={`programme-direction-${x}`} checked={p.directions.includes(x)} onChange={() => setP({ ...p, directions: p.directions.includes(x) ? p.directions.filter((y) => y !== x) : [...p.directions, x].sort() })} />
                <b>{x}</b> {t(`dir.${x}`)}
              </label>))}</div>
          </fieldset>
          {p.directions.map((x) => (
            <Field key={x} label={t("programme.direction_relevance", { dir: `${x} — ${t(`dir.${x}`)}` })} htmlFor={`dn-${x}`}>
              <Input id={`dn-${x}`} data-testid={`direction-note-${x}`} value={p.direction_notes?.[x] || ""} onChange={(e) => setP({ ...p, direction_notes: { ...p.direction_notes, [x]: e.target.value } })} />
            </Field>
          ))}
        </> : <dl className="md:col-span-2 grid gap-4 sm:grid-cols-3">
          <Meta label={t("field.objective")}>{prog.objective}</Meta><Meta label={t("programme.baseline")}>{prog.baseline_summary}</Meta>
          <Meta label={t("programme.observation_criteria")}>{prog.observation_criteria}</Meta><Meta label={t("programme.review_date")}>{fmtDate(prog.review_date)}</Meta>
          <Meta label={t("field.referral_source")}>{prog.referral_source}</Meta><Meta label={t("programme.completion_conditions")}>{prog.completion_conditions}</Meta>
          <Meta label={t("programme.directions")}>{prog.directions.map((x) => `${x} — ${t(`dir.${x}`)}`).join("; ")}</Meta>
          <Meta label={t("common.created")}>{fmtDateTime(prog.created_at)} · {prog.creator_name}</Meta>
          <Meta label={t("programme.visibility")}>{t("programme.private_default")}</Meta>
        </dl>}
      </Card>

      <Card data-testid="data-use-basis">
        <div className="mb-3 flex flex-wrap items-center gap-2"><h2 className="text-lg font-bold">{t("basis.title")}</h2><Tag data-testid="basis-draft-label" className="bg-[hsl(42_95%_90%)] text-[hsl(30_80%_24%)]">{t("basis.draft_label")}</Tag></div>
        <p className="mb-3 rounded-xl bg-muted p-3 text-sm">{t("basis.wording")}</p>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label={t("basis.confirmed_by")} htmlFor="b-by"><Input id="b-by" disabled={!draft} data-testid="basis-confirmed-by-input" value={basis.confirmed_by || ""} onChange={(e) => setP({ ...p, data_use_basis: { ...basis, confirmed_by: e.target.value } })} /></Field>
          <Field label={t("basis.method")} htmlFor="b-m"><Select id="b-m" disabled={!draft} data-testid="basis-method-select" value={basis.method || ""} onChange={(e) => setP({ ...p, data_use_basis: { ...basis, method: e.target.value } })}>
            <option value="">{t("common.choose")}</option>{["verbal", "written", "electronic"].map((m) => <option key={m} value={m}>{t(`basis.m_${m}`)}</option>)}</Select></Field>
          <Field label={t("basis.wording_version")} htmlFor="b-v"><Input id="b-v" disabled data-testid="basis-wording-version" value={basis.wording_version || ""} readOnly /></Field>
          <Field label={t("basis.date")} htmlFor="b-d"><Input id="b-d" type="date" disabled={!draft} data-testid="basis-date-input" value={basis.date || ""} onChange={(e) => setP({ ...p, data_use_basis: { ...basis, date: e.target.value } })} /></Field>
        </div>
      </Card>

      {draft ? <ProgrammeEditor p={p} setP={setP} products={products} setProducts={setProducts} errs={errs} dogId={d.dog.id} /> : <PlanView prog={prog} products={products} />}

      {!draft && <>
        <VisitsSection d={d} reload={load} />
        <HomeTasksSection d={d} reload={load} />
        {prog.status === "active" && <SendSection d={d} reload={load} />}
        <ApplicationsSection apps={d.applications} />
        <Card>
          <h2 className="mb-3 text-lg font-bold">{t("programme.editions")}</h2>
          <ul className="space-y-2" data-testid="programme-editions">{d.editions.map((e) => <li key={e.id} className="rounded-xl border bg-white p-3 text-sm"><b>{t("programme.edition_n", { n: e.edition })}</b> · {e.author_name} · {fmtDateTime(e.created_at)} · {t(`edition_reason.${e.reason}`, { defaultValue: e.reason })}</li>)}</ul>
        </Card>
      </>}
      <Card>
        <div className="flex items-center justify-between"><h2 className="text-lg font-bold">{t("history.title")}</h2>
          {!history && <Btn variant="outline" testid="programme-history-btn" onClick={() => api.get(`/programmes/${id}/history`).then((r) => setHistory(r.data))}>{t("history.show")}</Btn>}</div>
        {history && <div className="mt-3"><HistoryList items={history} /></div>}
      </Card>
      {draft && !prog.used && <Btn variant="danger" testid="programme-delete-btn" onClick={() => setDlg("delete")}>{t("programme.delete_draft")}</Btn>}

      <ConfirmDialog open={dlg === "activate"} onOpenChange={() => setDlg(null)} title={t("programme.approve")} consequence={t("programme.approve_consequence")} confirmLabel={t("programme.approve")} onConfirm={activate} testid="approve-dialog" />
      <ConfirmDialog open={dlg === "discontinue"} onOpenChange={() => setDlg(null)} title={t("programme.discontinue")} consequence={t("programme.discontinue_consequence")} reasonLabel={t("common.reason")} reasonRequired confirmLabel={t("programme.discontinue")} onConfirm={(reason) => post("discontinue", { reason })} testid="discontinue-dialog" />
      <ConfirmDialog open={dlg === "delete"} onOpenChange={() => setDlg(null)} title={t("programme.delete_draft")} consequence={t("programme.delete_consequence")} danger confirmLabel={t("programme.delete_draft")}
        onConfirm={() => api.delete(`/programmes/${id}`).then(() => navigate(`/dogs/${d.dog.id}`)).catch((e) => { throw new Error(t(`err.${parseError(e).code}`)); })} testid="delete-dialog" />
      <CompleteDialog open={dlg === "complete"} onOpenChange={() => setDlg(null)} objective={prog.objective} onConfirm={(results) => post("complete", { results })} />
    </div>
  );
}

function CompleteDialog({ open, onOpenChange, objective, onConfirm }) {
  const { t } = useTranslation();
  const [result, setResult] = useState("");
  return (
    <ConfirmDialog open={open} onOpenChange={onOpenChange} title={t("programme.complete")} consequence={t("programme.complete_consequence")} confirmLabel={t("programme.complete")} testid="complete-dialog"
      onConfirm={() => { if (!result.trim()) throw new Error(t("err.results_required")); return onConfirm([{ objective, result }]); }}>
      <Field label={t("programme.result_for", { objective })} htmlFor="res" required><Textarea id="res" data-testid="complete-result-input" value={result} onChange={(e) => setResult(e.target.value)} /></Field>
    </ConfirmDialog>
  );
}

function PlanView({ prog, products }) {
  const { t } = useTranslation();
  const pname = (id) => products.find((x) => x.id === id)?.name || id;
  return (
    <div className="space-y-4" data-testid="plan-view">
      {prog.stages.map((s, i) => (
        <Card key={s.id}>
          <h2 className="mb-3 text-lg font-bold">{t("programme.stage_n", { n: i + 1 })}: {s.title}</h2>
          {prog.directions.map((dir) => (
            <section key={dir} className="mb-4" data-testid={`direction-section-${dir}`}>
              <h3 className="mb-2 font-display font-bold">{dir} — {t(`dir.${dir}`)}</h3>
              {s.actions.filter((a) => a.direction === dir).map((a) => (
                <details key={a.id} className="mb-2 rounded-xl border bg-white p-3" data-testid={`plan-action-${a.id}`}>
                  <summary className="cursor-pointer"><b>{a.title}</b> <OriginLabel a={a} /></summary>
                  <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">{ACTION_FIELDS.map((k) => { const v = a.fields?.[k] || {}; return <Meta key={k} label={t(`afield.${k}`)}>{v.na ? `${t("common.na")} — ${v.na_reason}` : v.value}</Meta>; })}</dl>
                  {a.product_ids?.length > 0 && <p className="mt-2 text-sm">{t("programme.products")}: {a.product_ids.map(pname).join(", ")}</p>}
                </details>
              ))}
            </section>
          ))}
          {s.home_activities.length > 0 && <h3 className="mb-2 font-display font-bold">{t("programme.home_activities")}</h3>}
          {s.home_activities.map((h) => <div key={h.id} className="mb-2 rounded-xl border bg-[hsl(140_30%_97%)] p-3 text-sm"><b>{h.title}</b> · {h.protocol_id ? t("home_field.standard_block") : `${t("home_field.free_text")} (${t(`lang.${h.text_language || "en"}`)})`} · {h.frequency} · {t("home_field.stopping_conditions")}: {h.stopping_conditions}</div>)}
        </Card>
      ))}
    </div>
  );
}
